import { ref } from 'vue'
import { STORAGE_DASHBOARD_VERSION_KEY, STORAGE_TOKEN_KEY, URL_TOKEN_PARAM } from '@/enum/generic'
import GlobalCachedDataService from '@/core/services/cachedData/GlobalCachedDataService'
import { version } from '../../package.json'
import env from '@/env'
import { clearActiveDashboard } from '@/composables/globalData/useDashoardsData'
import { clearEntitiesState } from '@/composables/globalData/useEntitiesData'
import { clearAllTabSpecificData } from '@/composables/useTabSpecificStorage'

const currentUserToken = ref<string>('')
const isDev = import.meta.env.MODE === 'development'

export function getActiveToken () {
    return currentUserToken.value
}

/**
 * Sets the active token from URL or falls back to last used token
 * 
 * Token priority:
 * 1. Token from URL (?token=xxx)
 * 2. Last used token from localStorage (fallback)
 * 3. Redirect to login if neither exists
 * 
 * With token-based storage:
 * - Token = storage namespace
 * - Same token = same data (automatically)
 * - Different token = different data (automatically)
 */
export async function setActiveToken () {
    const urlParams = new URLSearchParams(window.location.search)
    const urlToken = urlParams.get(URL_TOKEN_PARAM) || ''
    
    // Fallback to last used token if no URL token
    const storedToken = localStorage.getItem(STORAGE_TOKEN_KEY) || ''
    const finalToken = urlToken || storedToken
    
    // Check for version changes (affects all tokens/users)
    const storageVersion = localStorage.getItem(STORAGE_DASHBOARD_VERSION_KEY)
    const versionChanged = storageVersion !== version

    if (versionChanged) {
        // Version upgrade: Clear ALL data (data structure may have changed)
        console.log('[CLEARING CACHE] Version changed - clearing all data')
        clearEntitiesState()
        clearActiveDashboard()
        await GlobalCachedDataService.clearAllData()
        clearAllTabSpecificData()
        localStorage.setItem(STORAGE_DASHBOARD_VERSION_KEY, version)
    }

    if (!finalToken) {
        return redirectToLogin()
    }
    
    // Save the token for future fallback (when URL has no token)
    localStorage.setItem(STORAGE_TOKEN_KEY, finalToken)

    currentUserToken.value = finalToken
    console.log('[Token] Active token set:', urlToken ? 'from URL' : 'from storage (fallback)')
}

export function redirectToLogin () {
    if (!isDev) {
        window.location.href = env.VITE_APP_FALLBACK_URL
    }
}
